import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AlertService,
  SelectBox,
  Button,
  Select,
  SelectOption,
  InformationIcon,
  Timing,
  Theme,
  IQSummary,
  MarketingMountainIcon,
  MarketingStarIcon,
  MiniTooltip,
} from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import { useMutation } from '@apollo/client';
import {
  TrucklaneFilterZeroState,
  TrucklaneNonSuggestionZeroState,
  TrucklaneSuggestionZeroState,
  TrucklaneExceptionZeroState,
  TrucklaneActiveOrInactiveNonSuggestionZeroState,
  TrucklaneSuggestionsAwardedZeroState,
} from './trucklane-zero-state';
import TrucklaneCard from './trucklane-card';
import {
  AcceptShiftedOfferListings,
  AwardReservePriceExceptions,
  AwardSuggestedOfferListings,
  UnawardOfferListingsByTruckLanes,
} from '../../graphql/mutations';
import { OfferComparisonStrings } from '../../string-resources';
import { offerlistingStatuses } from '../../enums';
import { buildSortOptions, sortPallet, sortRevenue, sortSuggestedRevenue, sortWeight } from './trucklane-filter-utility';
import { ActiveOfferShiftsQuery, AwardSummaryQuery, trucklanePollingQuery, trucklaneExceptionsQuery } from '../../graphql/queries';
import { Store } from '../../store';
import { invalidErrorCodes, TrucklaneActionInProgressContext } from './trucklane-utility';
import Tracking from '../../utilities/tracking-apis';
import OfferShiftContext, { formatShiftsForAcceptance } from '../offer-comparison/offer-shift-context';
import { checkFeature } from '../../helpers';
import { featureFlags } from '../../enums';
import TrucklaneSearch from './trucklane-search';
import { ACTIVE_STEP } from '../offer-comparison/active-step-enums';
import roundNumber from '../../utilities/round';
import dataExportService from '../../services/data-export-service';
import accounting from 'accounting';
import ReservePriceThresholdSettings from '../../components/actions/reserve-price-threshold-settings';

const styles = {
  row: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rowStacked: {
    '@media (max-width: 1580px)': {
      overflow: 'auto',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    margin: '24px 0px 16px',
    gap: '6px',
  },
  childRow: {
    display: 'flex',
    alignItems: 'center',
  },
  wrappingRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    '@media (max-width: 1580px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '12px',
    },
  },
  selectBox: {
    marginRight: '8px',
  },
  sort: {
    minWidth: '260px',
  },
  title: {
    fontSize: 16,
    fontWeight: '500',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& img': {
      height: 150,
      width: 300,
    },
    '& h1': {
      extend: 'title',
      marginTop: '10px',
      marginBottom: '48px',
    },
  },
  filter: {
    extend: 'row',
    backgroundColor: Theme.teal10,
    marginTop: 24,
    '& h1': {
      fontSize: 14,
      fontWeight: 'normal',
      margin: '12px 8px',
      textWrap: 'nowrap',
    },
    '& svg': {
      maxWidth: 16,
      maxHeight: 16,
      width: 16,
      height: 16,
      margin: '13px 0px 11px 12px',
      fill: Theme.infoColor,
    },
    '& a': {
      backgroundColor: 'transparent',
      color: Theme.infoColor,
      fontSize: 14,
      marginRight: 12,
      '&:hover': {
        cursor: 'pointer',
      },
    },
  },
  buttonIcon: {
    '& svg': {
      maxWidth: 20,
      maxHeight: 20,
      width: 20,
      height: 20,
      marginTop: 2,
    },
  },
  stepIndicator: {
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 800,
    lineHeight: '16px',
    textTransform: 'uppercase',
  },
  primaryColor: {
    color: Theme.primaryColor,
  },
  exceptionColor: {
    color: Theme.teal,
  },
  noSuggestionsColor: {
    color: Theme.grey80,
  },
  blockTitle: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: 12,
    },
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  titleText: {
    fontSize: 20,
    fontWeight: '700',
  },
  selectWithButton: {
    display: 'flex',
    '@media (max-width: 1580px)': {
      order: 1,
    },
  },
  opportunitiesPageActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: 24,
  },
  tooltip: {
    width: '240px',
    textWrap: 'wrap',
    height: 'fit-content',
  },
};
const useStyles = createUseStyles(styles);

const TrucklaneBlock = ({
  title,
  trucklaneData,
  buttonDetails,
  history,
  user,
  filters,
  clearFilters,
  openDrilldown,
  metrics,
  handleSearch,
  searchText,
  toggleFilters,
  filtersAppliedCount,
  activeStep,
  setActiveStep,
  recalculation,
  unfilteredSuggestedTrucklanesCount,
}) => {
  let cache = null;
  // A lane is recalculating when its channel is flagged invalid in the store. This replaces the old
  // approach of writing `status: 'Invalid'` back into the Apollo cache, which forced a full re-render
  // of the lane tree on every poll. `recalculation` is threaded in purely as a render trigger.
  const isLaneRecalculating = (trucklane) => Store.invalidTrucklaneChannels.has(`${trucklane.truckType}-${trucklane.origin}`);
  const reservePriceExceptionsEnabled = checkFeature(featureFlags.reservePriceExceptions);
  const exportEnabled = checkFeature(featureFlags.awardingOpportunitiesExport);
  const [awardOffersMutation, { loading: awardOffersLoading }] = useMutation(AwardSuggestedOfferListings);
  const [unawardOffersMutation, { loading: unawardOffersLoading }] = useMutation(UnawardOfferListingsByTruckLanes);
  const [acceptOfferShifts, { loading: acceptingOfferShiftLoading }] = useMutation(AcceptShiftedOfferListings);
  const [awardOpportunitiesMutation, { loading: awardOpportunitiesLoading }] = useMutation(AwardReservePriceExceptions);
  const suggested = activeStep === ACTIVE_STEP.SUGGESTIONS || activeStep === ACTIVE_STEP.OPPORTUNITIES;
  const classes = useStyles();
  const [sort, setSort] = useState(null);
  const [selected, setSelected] = useState([]);
  const [slideCard, setSlideCard] = useState(false);
  const [redirectToOpportunitiesAfterAward, setRedirectToOpportunitiesAfterAward] = useState(false);
  const sortOptions = buildSortOptions(suggested);
  const { setActionInProgress } = useContext(TrucklaneActionInProgressContext);
  const { offerShiftsMappedToTrucklanes = new Map() } = useContext(OfferShiftContext) || {};

  const handleSortChange = useCallback(
    (_, value) => {
      if (suggested) {
        localStorage.setItem('trucklaneSuggestedSort', JSON.stringify(value));
      } else {
        localStorage.setItem('trucklaneNonSuggestedSort', JSON.stringify(value));
      }
      setSort(value);
    },
    [sort],
  );

  const getSortFromLocalStorage = () => {
    const localStorageKey = suggested ? 'trucklaneSuggestedSort' : 'trucklaneNonSuggestedSort';
    const sortValue = localStorage.getItem(localStorageKey);
    return sortValue ? JSON.parse(sortValue) : null;
  };

  const getDefaultSort = () => {
    const valueBase = suggested ? 'SUGGESTED_' : '';
    const textBase = suggested ? 'Suggested ' : '';
    const mode = user.site?.trucklaneMinimums?.mode;

    if (mode === 'TOTAL_REVENUE') {
      return { value: `HIGHEST_${valueBase}REV`, text: `Highest ${textBase}Revenue` };
    }

    return { value: `HIGHEST_${valueBase}NET_REV`, text: `Highest ${textBase}Net Revenue` };
  };

  useEffect(() => {
    setSort(getSortFromLocalStorage() || getDefaultSort());
  }, [user]);

  useEffect(() => {
    if (!redirectToOpportunitiesAfterAward) return;
    if (activeStep !== ACTIVE_STEP.SUGGESTIONS) {
      setRedirectToOpportunitiesAfterAward(false);
      return;
    }
    if (reservePriceExceptionsEnabled && !trucklaneData?.length && !unfilteredSuggestedTrucklanesCount) {
      setActiveStep(ACTIVE_STEP.OPPORTUNITIES);
      setRedirectToOpportunitiesAfterAward(false);
    }
  }, [
    redirectToOpportunitiesAfterAward,
    trucklaneData,
    unfilteredSuggestedTrucklanesCount,
    activeStep,
    reservePriceExceptionsEnabled,
    setActiveStep,
  ]);

  const sortedTrucklanes = useMemo(() => {
    if (sort) {
      const netMode = ['HIGHEST_NET_REV', 'LOWEST_NET_REV', 'HIGHEST_SUGGESTED_NET_REV', 'LOWEST_SUGGESTED_NET_REV'].includes(sort.value);
      const reverse = [
        'LOWEST_NET_REV',
        'LOWEST_REV',
        'LOWEST_SUGGESTED_NET_REV',
        'LOWEST_SUGGESTED_REV',
        'LOWEST_WEIGHT',
        'LOWEST_SUGGESTED_WEIGHT',
        'LOWEST_PALLET',
        'LOWEST_SUGGESTED_PALLET',
      ].includes(sort.value);
      const suggestionType = activeStep === ACTIVE_STEP.OPPORTUNITIES ? 'opportunity' : activeStep === ACTIVE_STEP.SUGGESTIONS ? 'award' : null;
      let sortedData = [];
      if (['HIGHEST_SUGGESTED_NET_REV', 'LOWEST_SUGGESTED_NET_REV', 'HIGHEST_SUGGESTED_REV', 'LOWEST_SUGGESTED_REV'].includes(sort.value)) {
        sortedData = sortSuggestedRevenue(trucklaneData, netMode, suggestionType);
      } else if (['HIGHEST_WEIGHT', 'LOWEST_WEIGHT', 'HIGHEST_SUGGESTED_WEIGHT', 'LOWEST_SUGGESTED_WEIGHT'].includes(sort.value)) {
        sortedData = sortWeight(trucklaneData, suggestionType);
      } else if (sort.value.includes('PALLET')) {
        sortedData = sortPallet(trucklaneData, suggestionType);
      } else {
        sortedData = sortRevenue(trucklaneData, netMode);
      }
      if (reverse) return sortedData.every((l) => l.value === 0) ? sortedData.map((l) => l.trucklane) : sortedData.reverse().map((l) => l.trucklane);
      return sortedData.map((l) => l.trucklane);
    }
    return trucklaneData;
  }, [sort, trucklaneData, activeStep]);

  const emptyState = useMemo(() => {
    if (!trucklaneData?.length && filters) return <TrucklaneFilterZeroState />;
    if (!trucklaneData?.length && !suggested && !reservePriceExceptionsEnabled) return <TrucklaneNonSuggestionZeroState />;
    if (!trucklaneData?.length && !reservePriceExceptionsEnabled) return <TrucklaneSuggestionZeroState history={history} />;
    if (!trucklaneData?.length && reservePriceExceptionsEnabled && activeStep === ACTIVE_STEP.SUGGESTIONS)
      return <TrucklaneSuggestionsAwardedZeroState setActiveStep={setActiveStep} />;
    if (!trucklaneData?.length && reservePriceExceptionsEnabled && activeStep === ACTIVE_STEP.OPPORTUNITIES)
      return <TrucklaneExceptionZeroState history={history} setActiveStep={setActiveStep} />;
    if (
      !trucklaneData?.length &&
      reservePriceExceptionsEnabled &&
      (activeStep === ACTIVE_STEP.ACTIVE_LANES || activeStep === ACTIVE_STEP.INACTIVE_LANES)
    )
      return <TrucklaneActiveOrInactiveNonSuggestionZeroState activeStep={activeStep} />;
    return <></>;
  }, [trucklaneData, activeStep]);

  const buildSort = useMemo(() => {
    return (
      <Select classes={{ select__container: classes.sort }} label="Sort By" selectedItem={sort} onChange={handleSortChange.bind(this)}>
        {sortOptions.map((option) => (
          <SelectOption key={option.value} value={option.value}>
            {option.title}
          </SelectOption>
        ))}
      </Select>
    );
  }, [sort]);

  const selectableTrucklaneIds = useMemo(() => {
    if (activeStep === ACTIVE_STEP.SUGGESTIONS) {
      // `recalculation` is in the dep list so this recomputes when the store's invalid set changes.
      return trucklaneData
        .filter((trucklane) => !Store.invalidTrucklaneChannels.has(`${trucklane.truckType}-${trucklane.origin}`))
        .map((trucklane) => trucklane.id);
    }
    if (activeStep === ACTIVE_STEP.OPPORTUNITIES) {
      return trucklaneData
        .filter((trucklane) =>
          trucklane.offers.some((offer) => offer.suggestions?.reservePriceException?.suggested && offer.status === offerlistingStatuses.ACTIVE),
        )
        .map((trucklane) => trucklane.id);
    }
    return trucklaneData
      .filter((trucklane) => trucklane.offers.filter((offer) => offer.status === offerlistingStatuses.AWARDED).length)
      .map((trucklane) => trucklane.id);
  }, [trucklaneData, activeStep, recalculation, Store.invalidTrucklaneChannels.size]);

  const allSelected = () => {
    return trucklaneData && selected.length > 0 && selected.length === selectableTrucklaneIds.length;
  };

  const partialSelected = () => {
    return trucklaneData && selected.length > 0 && selected.length < selectableTrucklaneIds.length;
  };

  const selectAll = (result) => {
    setSlideCard(false);
    setSelected(result.checked ? selectableTrucklaneIds || [] : []);
  };

  function handleSelect(e) {
    setSlideCard(false);
    if (e.checked) {
      setSelected([...selected, e.value]);
    } else {
      setSelected(selected.filter((trucklaneId) => trucklaneId !== e.value));
    }
  }

  function deSelect(trucklaneId, singleDeselect = false) {
    if (singleDeselect) {
      setSelected(selected.filter((selectedIds) => selectedIds !== trucklaneId));
    } else {
      const deSelectedTrucklane = trucklaneData.find((trucklane) => trucklane.id === trucklaneId);
      const filteredLanes = trucklaneData
        .filter((trucklane) => trucklane.origin === deSelectedTrucklane.origin && trucklane.truckType === deSelectedTrucklane.truckType)
        .map((trucklane) => trucklane.id);
      setSelected(selected.filter((selectedIds) => !filteredLanes.includes(selectedIds)));
    }
  }

  const errorRefresh = () => {
    window.location.reload();
  };

  const trucklaneOfferShifts = useMemo(() => {
    if (offerShiftsMappedToTrucklanes && trucklaneData) {
      const trucklaneShifts = [];
      trucklaneData.forEach((td) => {
        const shifts = offerShiftsMappedToTrucklanes.get(td.id);
        if (shifts) trucklaneShifts.push(...shifts);
      });
      return trucklaneShifts;
    }
    return [];
  }, [offerShiftsMappedToTrucklanes, trucklaneData]);

  const acceptTrucklaneOfferShifts = async () => {
    if (trucklaneOfferShifts.length > 0) {
      const refetchQueries = [
        {
          query: AwardSummaryQuery,
        },
        {
          query: ActiveOfferShiftsQuery,
        },
        {
          query: trucklanePollingQuery,
          variables: {
            pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({ origin: channel.originDCId, truckType: channel.truckType })),
          },
        },
      ];
      const acceptShiftResult = await acceptOfferShifts({
        variables: {
          offerShifts: formatShiftsForAcceptance(trucklaneOfferShifts),
        },
        refetchQueries,
      });
      if (acceptShiftResult.data?.acceptShiftedOfferListings.errors && acceptShiftResult.data.acceptShiftedOfferListings.errors.length > 0) {
        throw new Error(acceptShiftResult.data.acceptShiftedOfferListings.errors[0].message);
      }
    }
  };

  const awardSuggestions = async () => {
    const trucklaneIds = [];
    const offerIds = trucklaneData
      .filter((trucklane) => {
        if (selected.includes(trucklane.id)) {
          trucklaneIds.push(trucklane.id);
          return true;
        }
        return false;
      })
      .flatMap((trucklane) => trucklane.offers)
      .filter((offer) => offer.suggestions?.award?.suggested && offer.status === offerlistingStatuses.ACTIVE)
      .map((offer) => offer._id);
    if (offerIds.length === 0) return;
    const resp = await awardOffersMutation({
      variables: { offerListingIds: offerIds },
      update: (cacheResult) => {
        cache = cacheResult;
      },
      refetchQueries: [
        {
          query: AwardSummaryQuery,
        },
        {
          query: trucklanePollingQuery,
          variables: {
            pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({ origin: channel.originDCId, truckType: channel.truckType })),
          },
        },
      ],
    });
    if (resp.data?.awardSuggestedOfferListings?.errors?.length) {
      const recalcError = resp.data?.awardSuggestedOfferListings?.errors.some((error) => invalidErrorCodes.includes(error.code));
      if (recalcError) {
        Tracking.Event('There was a recalculation based error while awarding', {
          userId: user._id,
        });
        return AlertService.alert({
          type: 'warning',
          autoDismiss: false,
          message: (
            <div>
              <span>{OfferComparisonStrings.awardingError}</span>
              <div>
                <h1 className="error_action" onClick={errorRefresh}>
                  Refresh Suggested Awards
                </h1>
              </div>
            </div>
          ),
        });
      }
      throw Error();
    }
    AlertService.alert({ type: 'success', autoDismiss: true, message: <span>Suggested Offers have been awarded</span> });
    setSlideCard(true);
    if (trucklaneIds.length === trucklaneData.length) setRedirectToOpportunitiesAfterAward(true);
    Tracking.Event('Offers were bulk awarded with suggestions in trucklane view', {
      userId: user._id,
    });
    return setTimeout(() => {
      trucklaneIds.forEach((laneId) => {
        cache.modify({
          id: cache.identify({ id: laneId, __typename: 'Trucklane' }),
          fields: {
            hasSuggestions() {
              return false;
            },
          },
        });
        setSelected([]);
      });
    }, 500);
  };

  const unawardOffers = async () => {
    const selectedTrucklanes = trucklaneData.filter((trucklane) => selected.includes(trucklane.id));
    const refetchQueries = [
      {
        query: AwardSummaryQuery,
      },
      {
        query: trucklanePollingQuery,
        variables: {
          pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({ origin: channel.originDCId, truckType: channel.truckType })),
        },
      },
      {
        query: ActiveOfferShiftsQuery,
      },
    ];
    const trucklanes = selectedTrucklanes.map((trucklane) => {
      return {
        buyerSiteId: trucklane.offers[0]?.buyerSiteId || '',
        truckType: trucklane.offers[0]?.truckType || '',
        logisticsTerm: trucklane.offers[0]?.logisticsTerm || '',
        transactionLocationId: trucklane.offers[0]?.transactionLocationId || '',
        sellerLocationId: trucklane.offers[0]?.sellerLocationId || '',
      };
    });
    const results = await unawardOffersMutation({
      variables: { trucklanes },
      refetchQueries,
    });
    if (results.data?.UnawardOfferListingsByTruckLanes?.errors?.length) throw Error();
    AlertService.alert({ type: 'success', autoDismiss: true, message: <span>Offers have been unawarded</span> });
    setSelected([]);
    Tracking.Event('Offers were bulk unawarded in trucklane view', {
      userId: user._id,
    });
  };

  const bulkAwardOpportunities = async () => {
    const offerIds = trucklaneData
      .filter((trucklane) => selected.includes(trucklane.id))
      .flatMap((trucklane) => trucklane.offers)
      .filter((offer) => offer.suggestions?.reservePriceException?.suggested && offer.status === offerlistingStatuses.ACTIVE)
      .map((offer) => offer._id);
    if (offerIds.length === 0) return;
    const resp = await awardOpportunitiesMutation({
      variables: { offerListingIds: offerIds },
      refetchQueries: [
        { query: AwardSummaryQuery },
        {
          query: trucklanePollingQuery,
          variables: {
            pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({ origin: channel.originDCId, truckType: channel.truckType })),
          },
        },
        { query: trucklaneExceptionsQuery },
      ],
    });
    if (resp.data?.awardReservePriceExceptions?.errors?.length) throw Error();
    AlertService.alert({ type: 'success', autoDismiss: true, message: <span>Suggested Opportunities have been awarded</span> });
    setSelected([]);
    Tracking.Event('Opportunities were bulk awarded in trucklane view', {
      userId: user._id,
      transactionCycleId: trucklaneData[0]?.offers[0]?.transactionCycleId || '',
    });
  };

  const awardOrUnaward = Timing.throttle(() => {
    if (activeStep === ACTIVE_STEP.SUGGESTIONS) {
      (async () => {
        try {
          setActionInProgress(true);
          await acceptTrucklaneOfferShifts();
          await awardSuggestions();
        } catch {
          AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.awardingError}</span> });
        } finally {
          setActionInProgress(false);
        }
      })();
    } else if (activeStep === ACTIVE_STEP.OPPORTUNITIES) {
      (async () => {
        try {
          setActionInProgress(true);
          await bulkAwardOpportunities();
        } catch {
          AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.awardingError}</span> });
        } finally {
          setActionInProgress(false);
        }
      })();
    } else {
      (async () => {
        try {
          setActionInProgress(true);
          await unawardOffers();
        } catch {
          AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.unawardingError}</span> });
        } finally {
          setActionInProgress(false);
        }
      })();
    }
  }, 1000);

  const selectAllIsDisabled = () => {
    if (activeStep === ACTIVE_STEP.SUGGESTIONS) {
      return !trucklaneData?.filter((trucklane) => !isLaneRecalculating(trucklane)).length;
    }
    if (activeStep === ACTIVE_STEP.OPPORTUNITIES) {
      return !trucklaneData
        ?.flatMap((trucklane) => trucklane.offers)
        .some((offer) => offer.suggestions?.reservePriceException?.suggested && offer.status === offerlistingStatuses.ACTIVE);
    }
    return !trucklaneData?.flatMap((trucklane) => trucklane.offers).filter((offer) => offer.status === offerlistingStatuses.AWARDED).length;
  };

  const buildIqSummary = () => {
    let netRevenue = 0;
    let soldCostRecovery = 0;
    let soldCostRecoveryChange = 0;
    metrics.forEach((metric) => {
      if (metric.title === 'Net Revenue') {
        const parseMoney = (s) => Number.parseFloat(String(s).replace(/[^0-9.-]/g, ''));
        netRevenue = roundNumber(parseMoney(metric.projectedExceptions) - parseMoney(metric.current), 2);
      }
      if (metric.title === 'Sold Cost Recovery (SCR)') {
        soldCostRecovery = metric.projectedExceptions;
        soldCostRecoveryChange = roundNumber(Number.parseFloat(metric.projectedExceptions) - Number.parseFloat(metric.current), 2);
      }
    });
    let laneActivationContext = 'activating new lanes and adding to active lanes';
    let newTrucklanesCount = 0;
    let activeTrucklanesCount = 0;
    trucklaneData.forEach((trucklane) => {
      if (trucklane.offers.some((offer) => [offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status))) {
        activeTrucklanesCount += 1;
      } else {
        newTrucklanesCount += 1;
      }
    });
    if (newTrucklanesCount === 0) {
      laneActivationContext = 'adding to active lanes';
    } else if (activeTrucklanesCount === 0) {
      laneActivationContext = 'activating new lanes';
    }

    return `By ${laneActivationContext}, you can generate ${accounting.formatMoney(netRevenue)} in additional net revenue with an average truck lane SCR of ${soldCostRecovery}, and a cycle SCR change of ${soldCostRecoveryChange > 0 ? `+${soldCostRecoveryChange}` : soldCostRecoveryChange}pp.`;
  };

  const renderBlockTitle = (stepText, colorClass, Icon) => (
    <div className={classes.blockTitle}>
      {Icon && <Icon style={{ width: 24, height: 24 }} />}
      <div className={classes.titleContainer}>
        <div className={`${classes.stepIndicator} ${colorClass}`}>{stepText}</div>
        <div className={classes.titleText}>{title}</div>
      </div>
    </div>
  );

  const buildTitle = () => {
    if (reservePriceExceptionsEnabled && activeStep === ACTIVE_STEP.SUGGESTIONS) {
      return renderBlockTitle('Step 1', classes.primaryColor, MarketingStarIcon);
    }
    if (reservePriceExceptionsEnabled && activeStep === ACTIVE_STEP.OPPORTUNITIES) {
      return renderBlockTitle('Step 2', classes.exceptionColor, MarketingMountainIcon);
    }
    if (reservePriceExceptionsEnabled && (activeStep === ACTIVE_STEP.ACTIVE_LANES || activeStep === ACTIVE_STEP.INACTIVE_LANES)) {
      return renderBlockTitle('NO SUGGESTIONS', classes.noSuggestionsColor, false);
    }
    return (
      <h1 className={classes.title}>
        {title} ({trucklaneData.length})
      </h1>
    );
  };

  const exportErrored = () => {
    const message =
      'Sorry we are unable to export awarding opportunities. If this problem persists, please contact a Spoiler Alert Administrator to help you.';
    AlertService.alert({ type: 'warning', message: <span>{message}</span> });
  };

  const exportCompleted = (response) => {
    if (response.ok || response.data) {
      const message = 'Your export is now processing and we will email you when it is completed.';
      AlertService.alert({ type: 'info', message: <span>{message}</span>, autoDismiss: true, dismissDelay: 5000 });
    } else {
      exportErrored();
    }
  };

  const handleOpportunitiesExport = Timing.debounce(() => {
    Tracking.Event('Exporting reserve price exceptions in trucklane view', {
      userId: user._id,
      transactionCycleId: trucklaneData[0]?.offers[0]?.transactionCycleId || '',
    });
    dataExportService.export('awarding-opportunities').then(exportCompleted).catch(exportErrored);
  }, 300);

  return (
    <div className={classes.block}>
      <div className={classes.opportunitiesPageActions}>
        {buildTitle()}
        <div className={classes.childRow} style={{ gap: 8 }}>
          {activeStep === ACTIVE_STEP.OPPORTUNITIES && <ReservePriceThresholdSettings site={user.site} />}
          {activeStep === ACTIVE_STEP.OPPORTUNITIES && exportEnabled && (
            <MiniTooltip
              text={<div className={classes.tooltip}>Export reserve price exceptions in an excel sheet to share with stakeholders for approval.</div>}
              position="custom"
              positionOffset={{ top: 'calc(100% + 2px)', right: -16 }}
            >
              <Button secondary testId="export-approval" onClick={handleOpportunitiesExport} disabled={!trucklaneData?.length}>
                Export for Approval
              </Button>
            </MiniTooltip>
          )}
        </div>
      </div>
      {reservePriceExceptionsEnabled && activeStep === ACTIVE_STEP.OPPORTUNITIES && trucklaneData?.length > 0 && (
        <IQSummary title="iQ SUMMARY" content={buildIqSummary()} />
      )}
      <div className={`${classes.row} ${reservePriceExceptionsEnabled ? classes.rowStacked : ''}`}>
        <div className={reservePriceExceptionsEnabled ? classes.wrappingRow : classes.childRow}>
          {activeStep !== ACTIVE_STEP.INACTIVE_LANES && (
            <div className={classes.selectWithButton}>
              <SelectBox
                disabled={selectAllIsDisabled()}
                className={classes.selectBox}
                partial={partialSelected()}
                all={allSelected()}
                onSelect={selectAll}
              />
              <Button
                onClick={awardOrUnaward}
                loading={awardOffersLoading || unawardOffersLoading || acceptingOfferShiftLoading || awardOpportunitiesLoading}
                loadingText={buttonDetails.loadingText}
                disabled={!trucklaneData.length || !selected.length}
                warning={buttonDetails.warning}
                icon={buttonDetails.icon || null}
                className={classes.buttonIcon}
                iq={suggested || false}
              >
                {buttonDetails.title}
              </Button>
            </div>
          )}
          {reservePriceExceptionsEnabled ? (
            <TrucklaneSearch
              handleSearch={handleSearch}
              toggleFilters={toggleFilters}
              filtersAppliedCount={filtersAppliedCount}
              fullScreen={false}
              searchText={searchText}
            />
          ) : null}
        </div>
        {buildSort}
      </div>
      {filters && (
        <div className={classes.filter}>
          <div className={classes.childRow}>
            <InformationIcon />
            <h1>Some truck lanes are hidden from view based on your filters or search</h1>
          </div>
          <a onClick={clearFilters}>Clear All Filters</a>
        </div>
      )}
      {emptyState}
      {sortedTrucklanes?.length > 0 &&
        sortedTrucklanes.map((trucklane) => (
          <TrucklaneCard
            key={trucklane.id}
            trucklaneId={trucklane.id}
            activeStep={activeStep}
            offers={trucklane.offers}
            recalculation={isLaneRecalculating(trucklane)}
            selected={selected.includes(trucklane.id)}
            deSelect={deSelect}
            handleSelect={handleSelect}
            awarded={selected.includes(trucklane.id) && slideCard}
            user={user}
            history={history}
            openDrilldown={openDrilldown}
            buyerMinimums={trucklane.buyerMinimums}
          />
        ))}
    </div>
  );
};

TrucklaneBlock.propTypes = {
  title: PropTypes.string,
  trucklaneData: PropTypes.array,
  buttonDetails: PropTypes.object,
  history: PropTypes.object,
  user: PropTypes.object,
  filters: PropTypes.bool,
  clearFilters: PropTypes.func,
  openDrilldown: PropTypes.func,
  activeStep: PropTypes.string,
  metrics: PropTypes.array,
  setActiveStep: PropTypes.func,
  handleSearch: PropTypes.func,
  searchText: PropTypes.string,
  toggleFilters: PropTypes.func,
  filtersAppliedCount: PropTypes.number,
  recalculation: PropTypes.bool,
  unfilteredSuggestedTrucklanesCount: PropTypes.number,
};

export default TrucklaneBlock;
